import { fallbackCharacterOptions } from '../fallbacks.js';
import { formatActor, formatLocation, formatPeriod } from '../format.js';
import { buildInfluenceBlock, OPERATION_GUARDRAILS } from '../influence.js';
import type { PromptRequest, PromptSourceBundle } from '../types.js';
import { baseRequest, type CompilerSpec } from './base.js';
import { buildWorldContext, filterActors } from '../filters.js';

const SPEC: CompilerSpec = {
  purpose: 'character_options',
  maxTier: 0,
  allowedTiers: [0],
  budget: { inputTokens: 1000, outputTokens: 600 },
  provider: 'deepseek',
};

export function compileCharacterOptions(
  source: PromptSourceBundle,
  draftActorId: string,
): PromptRequest {
  const world = buildWorldContext(source);
  const actor = filterActors(source.allActors, [draftActorId])[0];
  const actors = actor ? [actor] : [];
  const influence = buildInfluenceBlock('character_options', world, actors);
  const species = actor?.species ?? 'unspecified';
  const sexLabel = actor?.sex === 'F' ? 'female' : actor?.sex === 'M' ? 'male' : 'unspecified';
  const header = [
    'PURPOSE: character_options',
    OPERATION_GUARDRAILS.character_options,
    'FORBIDDEN: infer race/ethnicity, religion, disability, diagnosis, or other sensitive identity from appearance.',
    'TASK: Build a finite appearance option set for this species and sex.',
    'Human male → clothing a human man would wear in this period and place: shirts, t-shirts, jackets, coats, trousers, jeans, shorts, and hair. Never shells, scales, carapace, membrane, fur, or other non-human body parts.',
    'Human female → clothing a human woman would wear in this period and place: blouses, shirts, dresses, skirts, trousers, jackets, and hair. Never shells, scales, carapace, membrane, fur, or other non-human body parts.',
    'Dog → fur/coat types and dog-plausible colors/markings; tops/bottoms only when sensible (vest, harness, booties).',
    'Alien, fish, and other non-human → species-plausible slots; omit human-only clothing when irrelevant.',
    'Return appearanceOptions with slot hair|fur|top|bottom|color|shoes and short lowercase labels (4–8 per relevant slot).',
    'This set freezes after first build — be complete and internally consistent for the species and sex.',
    influence.text,
  ].join('\n');

  const tier0 = [
    `LOCATION: ${formatLocation(world)}`,
    `PERIOD: ${formatPeriod(world.timePeriod)}`,
    `SPECIES: ${species}`,
    `SEX: ${sexLabel}`,
    actor ? `CHARACTER_DRAFT: ${formatActor(actor)}` : 'CHARACTER_DRAFT: (new)',
  ].join('\n');

  return baseRequest(
    source,
    SPEC,
    { tier0 },
    actor ? [actor.actorId] : [],
    [],
    [],
    header,
    {
      local: fallbackCharacterOptions,
      retryOnValidationError: true,
      noKeyUsesLocal: true,
    },
  );
}

export const characterOptionsMaxTier = SPEC.maxTier;
